import { convertFiltersToApiParams, SortDirection } from '@features/ui-filters/api/filterUtils';
import { userFromBack } from '@root/src/entities/User/responseFormatters';
import { UserRequest } from '@root/src/entities/User/schema';
import { UserBack, UserFront } from '@root/src/entities/User/types';
import { PaginatedResponse } from '@root/src/types/common';
import { mapPaginatedResponse } from '@root/src/utils/formatters/responseAdapters';
import { fetchRequest, RequestMethod } from '@services/api/utils/utils';

const register = async (invitationToken: string, password: string): Promise<void> =>
  fetchRequest<{ invitationToken: string; password: string }, void, { invitationToken: string; password: string }>({
    endpoint: '/users',
    method: RequestMethod.POST,
    body: { invitationToken, password },
  });

const me = async (): Promise<UserFront | undefined> =>
  fetchRequest<UserBack | undefined, UserFront | undefined>({
    endpoint: '/users/me',
    method: RequestMethod.GET,
    responseFormatter: userFromBack,
  });

const changePassword = async (token: string, password: string): Promise<void> =>
  fetchRequest<{ token: string; password: string }, void, { token: string; password: string }>({
    endpoint: '/me/change-password',
    method: RequestMethod.POST,
    body: { token, password },
  });

const resetPassword = async (token: string, password: string): Promise<void> =>
  fetchRequest<{ token: string }, void, { token: string; password: string }>({
    endpoint: '/users/reset-password',
    method: RequestMethod.POST,
    body: { token, password },
  });

const forgotPassword = async (email: string): Promise<void> =>
  fetchRequest<{ email: string }, void, { email: string }>({
    endpoint: '/users/reset-password-query',
    method: RequestMethod.POST,
    body: { email },
  });

const createUser = async (data: UserRequest): Promise<UserFront> =>
  fetchRequest<UserBack, UserFront, UserRequest>({
    endpoint: '/users',
    method: RequestMethod.POST,
    body: data,
    responseFormatter: userFromBack,
  });

const updateUser = async (data: UserRequest, id: string): Promise<UserFront> =>
  fetchRequest<UserBack, UserFront, UserRequest>({
    endpoint: `/users/${id}`,
    method: RequestMethod.PUT,
    body: data,
    responseFormatter: userFromBack,
  });

const updateMe = async (data: UserRequest): Promise<UserFront> =>
  fetchRequest<UserBack, UserFront, UserRequest>({
    endpoint: `/users/me`,
    method: RequestMethod.PUT,
    body: data,
    responseFormatter: userFromBack,
  });

const deleteUser = async (id: string): Promise<void> =>
  fetchRequest<{ id: string }, void, { id: string }>({
    endpoint: `/users/${id}`,
    method: RequestMethod.DELETE,
  });

const getUsers = async (
  filters = {},
  page = 1,
  pageSize = 15,
  sort: { field: string; order: SortDirection }[] = [{ field: 'firstName', order: 'ASC' }],
  options: {
    includePocStats?: boolean;
    pocPreviewLimit?: number;
  } = {},
): Promise<PaginatedResponse<UserFront>> =>
  fetchRequest<PaginatedResponse<UserBack>, PaginatedResponse<UserFront>>({
    endpoint: '/users',
    method: RequestMethod.GET,
    query: convertFiltersToApiParams(filters, {
      page,
      pageSize,
      sorted: sort.map(({ field, order }) => ({
        id: field,
        desc: order,
      })),
      includePocStats: options.includePocStats,
      pocPreviewLimit: options.pocPreviewLimit,
    }),
    responseFormatter: data => mapPaginatedResponse(data, userFromBack),
  });

const getUser = async (id: string): Promise<UserFront> =>
  fetchRequest<UserBack, UserFront>({
    endpoint: `/users/${id}`,
    method: RequestMethod.GET,
    responseFormatter: userFromBack,
  });

export const userApi = {
  me,
  register,
  resetPassword,
  forgotPassword,
  changePassword,
  createUser,
  updateUser,
  getUsers,
  deleteUser,
  getUser,
  updateMe,
};
